import _ from 'lodash';
import { action, computed, observable, runInAction } from 'mobx';
import { PackageState } from '../../common/models';
import { RulesService } from '../../rules/services';
import { TestProjectsVisualStore } from '.';
import { TestProjectService } from '../services';
import { message } from 'antd';
import TestProjectInputSelectionStore from './TestProjectInputSelectionStore';
import TestProjectDataStore from './TestProjectDataStore';

// Owns project creation, document editing and baseline relocation.
// Existing-project input editing belongs to TestProjectInputEditorStore.
export default class TestProjectWizardStore {
    @observable
    inputSelectionStore: TestProjectInputSelectionStore | undefined;

    @observable.ref
    testProjectDataStore: TestProjectDataStore | undefined;

    @observable
    isCreationWizardVisible: boolean = false;

    @observable
    autocompleteValue: string = '';

    @observable
    searchTerm: string = '';

    @observable
    selectedTags: string[] = [];

    @observable
    selectedPackages: string[] = [];

    @observable
    testProjectUserTags: string[] = [];

    @observable
    availableTestProjectUserTags: string[] = [];

    @computed
    get currentTestProject() {
        return this.testProjectDataStore?.testProject;
    }

    @observable
    packagesEditDialogVisible: boolean = false;

    @observable
    progressDialogVisible: boolean = false;

    @observable
    sourcePackageForBaselineSwitch: string | undefined;

    @observable
    newPackagesForBaselineSwitch: string[] = [];

    @observable
    baselinesEditDialogVisible: boolean = false;

    @observable
    saveInProgress: boolean = false;

    @computed
    get filteredPackages() {
        const packages = this.visualStore.packages.filter(
            p => p.name.toLowerCase().includes(this.searchTerm.toLowerCase()) && p.state === PackageState.Ready
        );

        if (this.selectedTags.length > 0) {
            return packages.filter(p => p.userTags != null && p.userTags.some(t => this.selectedTags.includes(t)));
        }

        return packages;
    }

    @computed
    get allPackages() {
        return this.visualStore.packages.filter(p => p.state === PackageState.Ready);
    }

    @computed
    get autocompleteOptions() {
        const packageNames = [...new Set(this.visualStore.packages.map(p => p.name))];
        return packageNames.filter(p => p.toLowerCase().includes(this.autocompleteValue.toLowerCase()));
    }

    @computed
    get packageTags() {
        return [...new Set(_.flatten(this.visualStore.packages.filter(p => p.userTags != null).map(p => p.userTags)))];
    }

    @computed
    get allAvailableTags() {
        return [...new Set([...this.packageTags, ...this.availableTestProjectUserTags])];
    }

    @computed
    get currentProject() {
        return this.visualStore.currentProject;
    }

    @computed
    get isLoadingPackages() {
        return this.visualStore.loadingPackages;
    }

    @computed
    get loadingPackagesProgress() {
        return this.visualStore.loadingPackagesProgress;
    }

    @computed
    get loadingPackagesErrorMessage() {
        return this.visualStore.loadingPackagesErrorMessage;
    }

    constructor(
        private service: TestProjectService,
        private visualStore: TestProjectsVisualStore,
        private rulesService: RulesService
    ) {
        this.resetSelection();
    }

    @computed
    get isSettingUpTestProject() {
        return this.testProjectDataStore?.isLoadingTestProject ?? false;
    }

    // All existing-project mutations use this data owner, never the live project selection.
    @computed
    get canEditProject() {
        return this.testProjectDataStore?.canUseProject ?? false;
    }

    @computed
    get testProjectLoadError() {
        return this.testProjectDataStore?.testProjectLoadError;
    }

    isEditingProject(dataStore: TestProjectDataStore) {
        const testProjectDataStore = this.testProjectDataStore;

        return (
            testProjectDataStore?.projectId === dataStore.projectId &&
            testProjectDataStore.testProjectId === dataStore.testProjectId
        );
    }

    @action.bound
    async retryTestProjectSetup() {
        const testProjectDataStore = this.testProjectDataStore;

        if (testProjectDataStore && testProjectDataStore.projectId === this.currentProject?.id) {
            return this.setUpTestProject(testProjectDataStore.testProjectId);
        }

        return false;
    }

    @action.bound
    setIsCreationWizardVisible(isVisible: boolean) {
        if (isVisible) {
            if (this.inputSelectionStore?.projectId !== this.currentProject?.id) {
                this.resetSelection();
            }

            this.loadSelectionData();
        }

        this.isCreationWizardVisible = isVisible;
    }

    @action.bound
    async loadAllProjectPackages() {
        await this.visualStore.loadAllProjectPackages();
    }

    @action.bound
    setAutocompleteValue(value: string) {
        this.autocompleteValue = value;
    }

    @action.bound
    setSearchTerm(value: string) {
        this.searchTerm = value;
    }

    @action.bound
    resetWizard() {
        this.resetTestProjectSetup();
        this.saveInProgress = false;
        this.setAutocompleteValue('');
        this.setSearchTerm('');
        this.setSelectedPackages([]);
        this.resetSelection();
        this.setSelectedTags([]);
        this.testProjectUserTags = [];
        this.availableTestProjectUserTags = [];
    }

    @action.bound
    setSelectedTags(tags: string[]) {
        this.selectedTags = tags;
    }

    @action.bound
    setSelectedPackages(packages: string[]) {
        this.selectedPackages = packages;
    }

    @action.bound
    setTestProjectUserTags(tags: string[]) {
        this.testProjectUserTags = tags;
    }

    @action.bound
    async loadTestProjectUserTags() {
        if (!this.currentProject) {
            return;
        }

        try {
            const tags = await this.service.getTestProjectUserTags(this.currentProject.id);
            runInAction(() => {
                this.availableTestProjectUserTags = tags || [];
            });
        } catch (error) {
            console.error('Failed to load test project user tags', error);
        }
    }

    @action.bound
    async createTestProject(name: string, defaultBaselineFuzzy?: number) {
        const project = this.currentProject;
        const inputSelectionStore = this.inputSelectionStore;
        const inputsType = inputSelectionStore?.topicInputsType;

        if (!project || !inputSelectionStore || !inputsType) {
            return false;
        }

        try {
            this.saveInProgress = true;

            const packageIds = this.selectedPackages;
            const applicationId = inputsType === 'Bindings' ? inputSelectionStore.selectedAppForBindings : undefined;
            const inputGroupId =
                inputsType === 'Bindings' && inputSelectionStore.selectedInputGroupForBindings
                    ? inputSelectionStore.selectedInputGroupForBindings
                    : undefined;
            const topicIds = inputSelectionStore.topics;

            const resp = await this.service.createTestProject(
                project.id,
                name,
                packageIds,
                inputsType,
                topicIds,
                applicationId,
                inputGroupId,
                defaultBaselineFuzzy,
                this.testProjectUserTags
            );

            if (resp.isErr()) {
                message.error('Failed to create test project');
                return false;
            }

            this.setIsCreationWizardVisible(false);
            this.resetWizard();
            message.success('Test project created successfully');
            await this.visualStore.loadTestProjects();

            return true;
        } catch (error) {
            message.error('Failed to create test project');
            console.error(error);
            return false;
        } finally {
            this.saveInProgress = false;
        }
    }

    @action.bound
    async updateTestProject(name: string, defaultBaselineFuzzy?: number) {
        const testProject = this.currentTestProject;
        const inputSelectionStore = this.inputSelectionStore;
        const inputsType = inputSelectionStore?.topicInputsType;

        if (!this.canEditProject || !testProject || !inputSelectionStore || !inputsType) {
            return false;
        }

        try {
            this.saveInProgress = true;

            const testProjectId = testProject.id;
            const packageIds = this.selectedPackages;
            const applicationId = inputsType === 'Bindings' ? inputSelectionStore.selectedAppForBindings : undefined;
            const inputGroupId =
                inputsType === 'Bindings' && inputSelectionStore.selectedInputGroupForBindings
                    ? inputSelectionStore.selectedInputGroupForBindings
                    : undefined;
            const topicIds = inputSelectionStore.topics;

            const resp = await this.service.updateTestProject(
                testProject.projectId,
                testProjectId,
                name,
                packageIds,
                inputsType,
                topicIds,
                applicationId,
                inputGroupId,
                defaultBaselineFuzzy,
                this.testProjectUserTags
            );

            if (resp.isErr()) {
                message.error('Failed to update test project');
                return false;
            }

            this.setIsCreationWizardVisible(false);
            this.resetWizard();
            message.success('Test project updated successfully');
            await this.visualStore.loadTestProjects();

            return true;
        } catch (error) {
            message.error('Failed to updated test project');
            console.error(error);
            return false;
        } finally {
            this.saveInProgress = false;
        }
    }

    @action.bound
    async setUpTestProject(testProjectId: string) {
        const previousDataStore = this.testProjectDataStore;
        this.resetTestProjectSetup();

        const projectId = this.currentProject?.id;

        if (!projectId) {
            return false;
        }

        const testProjectDataStore = new TestProjectDataStore(projectId, testProjectId, this.service, this.visualStore);
        this.testProjectDataStore = testProjectDataStore;

        if (previousDataStore?.testProjectId !== testProjectId || this.inputSelectionStore?.projectId !== projectId) {
            this.resetSelection();
        }

        const loaded = await testProjectDataStore.loadTestProject();

        if (this.testProjectDataStore !== testProjectDataStore) {
            return false;
        }

        if (projectId !== this.currentProject?.id) {
            this.resetTestProjectSetup();
            return false;
        }

        const project = testProjectDataStore.testProject;

        if (!loaded || !project) {
            return false;
        }

        this.setSelectedPackages([...project.packageIds]);
        this.setTestProjectUserTags(project.userTags || []);
        this.inputSelectionStore?.initialize(project);
        return true;
    }

    @action.bound
    async updateTestProjectPackages() {
        const source = this.testProjectDataStore?.testProject;

        if (!source || !this.canEditProject) {
            return false;
        }

        try {
            const resp = await this.service.updateTestProjectPackages(
                source.projectId,
                source.id,
                this.selectedPackages
            );

            if (resp.isErr()) {
                message.error('Failed to update test project packages');
                return false;
            }

            message.success('Test project packages updated successfully');
            return true;
        } catch (err) {
            message.error('Failed to update test project packages');
            console.error(err);
            return false;
        }
    }

    @action.bound
    setPackagesEditDialogVisible(isVisible: boolean) {
        this.packagesEditDialogVisible = isVisible;

        if (!isVisible) {
            this.resetTestProjectSetup();
        }
    }

    @action.bound
    setSourcePackageForBaselineSwitch(packageId: string | undefined) {
        this.sourcePackageForBaselineSwitch = packageId;
    }

    @action.bound
    setNewPackagesForBaselineSwitch(packageIds: string[]) {
        this.newPackagesForBaselineSwitch = packageIds;
    }

    @action.bound
    resetBaselineSwitch() {
        this.setSourcePackageForBaselineSwitch(undefined);
        this.setNewPackagesForBaselineSwitch([]);
    }

    @action.bound
    setBaselinesEditDialogVisible(isVisible: boolean) {
        this.baselinesEditDialogVisible = isVisible;

        if (!isVisible) {
            this.resetTestProjectSetup();
        }
    }

    @action.bound
    getPackagesByIds(pkgIds: string[]) {
        const foundPackages = this.allPackages.filter(p => pkgIds.includes(p.id));
        const notFoundPackages = pkgIds
            .filter(id => !foundPackages.some(p => p.id === id))
            .map(id => ({ id, name: id }));
        return [...foundPackages, ...notFoundPackages];
    }

    @action.bound
    async switchBaselinesForPackage() {
        const source = this.testProjectDataStore?.testProject;

        if (!source || !this.canEditProject) {
            return false;
        }

        const sourcePackageId = this.sourcePackageForBaselineSwitch;

        if (!sourcePackageId || !this.newPackagesForBaselineSwitch.length) {
            return false;
        }

        const packageIds = this.newPackagesForBaselineSwitch;

        try {
            const resp = await this.service.moveBaselinesToPackages(
                source.projectId,
                source.id,
                sourcePackageId,
                packageIds
            );
            if (resp.isOk()) {
                message.success('Baselines updated');
                return true;
            } else {
                message.error('Failed to update baselines');
                return false;
            }
        } catch (err) {
            console.error(err);
            message.error('Failed to update baselines');
            return false;
        }
    }

    @action.bound
    private resetTestProjectSetup() {
        this.testProjectDataStore = undefined;
    }

    @action.bound
    private resetSelection() {
        const projectId = this.currentProject?.id;
        this.inputSelectionStore = projectId
            ? new TestProjectInputSelectionStore(projectId, this.service, this.rulesService, 'Tags')
            : undefined;
    }

    @action.bound
    private loadSelectionData() {
        this.inputSelectionStore?.loadMetadata();
        this.loadTestProjectUserTags();
    }
}
